import { useEffect, useMemo, useRef, useState } from 'react';
import { nomeCadastro, nomePessoa } from '../../nucleo/formato';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSinal, useTempoRealConectado } from '../../nucleo/tempo-real';
import { buscarPaginas, buscarTudo, CHAVE, useRecursos } from '../../componentes/agendamento-comum';

export { buscarPaginas, buscarTudo, CHAVE, diaLocal, useDisponibilidade, useRecursos, useServicos } from '../../componentes/agendamento-comum';
import type { Agendamento, Bloqueio } from './tipos';

/** Páginas de agendamentos por vez na visão: 10 de 200 (2.000). Acima disso a tela avisa e oferece carregar mais (r9 robustez B3). */
export const PAGINAS_AGENDA = 10;

/** Sem a conexão ao vivo (Redis fora, rede), a agenda volta a se atualizar por consulta. */
const SEM_CONEXAO_MS = 60_000;

/**
 * Agendamentos do período em lotes de `PAGINAS_AGENDA` páginas de 200 (r10 robustez B1): "Carregar mais" busca SÓ o lote
 * seguinte, a partir do cursor onde o anterior parou, e acumula (antes a chave mudava e tudo era baixado de novo desde a página
 * 1). `itens` junta os lotes; `parcial` quando o servidor ainda tem mais.
 */
export function useAgendamentos(de: Date, ate: Date, recursoId: string, ativo = true) {
  const params = { de: de.toISOString(), ate: ate.toISOString(), recurso_id: recursoId || undefined };
  const aoVivo = useTempoRealConectado();
  const q = useInfiniteQuery({
    enabled: ativo,
    refetchInterval: aoVivo ? false : SEM_CONEXAO_MS,
    queryKey: [CHAVE, 'agendamentos', params.de, params.ate, recursoId],
    queryFn: ({ pageParam }) => buscarPaginas<Agendamento>('/agenda/agendamentos', params, PAGINAS_AGENDA, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (lote) => (lote.parcial ? lote.cursor : null),
  });
  const itens = useMemo(() => q.data?.pages.flatMap((p) => p.itens) ?? [], [q.data]);
  return { ...q, itens, parcial: !!q.hasNextPage, carregandoMais: q.isFetchingNextPage };
}

/**
 * Ficha 2026-11-03: a sincronia com o Google avisa que algo mudou e a agenda aberta recarrega sozinha. Vários sinais em seguida (uma
 * rodada por pessoa) viram uma recarga só.
 */
export function useAgendaAoVivo() {
  const qc = useQueryClient();
  const espera = useRef<ReturnType<typeof setTimeout> | null>(null);
  useSinal('agenda', () => {
    if (espera.current) return;
    espera.current = setTimeout(() => {
      espera.current = null;
      void qc.invalidateQueries({ queryKey: [CHAVE] });
      void qc.invalidateQueries({ queryKey: ['/agenda/agendamentos'] });
    }, 500);
  });
}

let escolhaDaTela: string | null = null;
let limparEscolha: ReturnType<typeof setTimeout> | null = null;
/**
 * 03/11: o recurso escolhido no filtro. Trocar Semana, Dia e Mês remonta a visão, e a escolha continua; sair da tela volta à própria
 * agenda. Só em memória: a folga cobre a troca de aba, em que a visão nova monta logo depois de a anterior sair.
 */
export function useRecursoEscolhido(): [string | null, (v: string) => void] {
  const [valor, setValor] = useState(escolhaDaTela);
  useEffect(() => {
    if (limparEscolha) clearTimeout(limparEscolha);
    limparEscolha = null;
    return () => {
      limparEscolha = setTimeout(() => {
        escolhaDaTela = null;
        limparEscolha = null;
      }, 1500);
    };
  }, []);
  return [
    valor,
    (v) => {
      escolhaDaTela = v;
      setValor(v);
    },
  ];
}

export function useBloqueios(de: Date, ate: Date, recursoId: string, ativo = true) {
  const aoVivo = useTempoRealConectado();
  return useQuery({
    enabled: ativo,
    refetchInterval: aoVivo ? false : SEM_CONEXAO_MS,
    queryKey: [CHAVE, 'bloqueios', de.toISOString(), ate.toISOString(), recursoId],
    queryFn: () => buscarTudo<Bloqueio>('/agenda/bloqueios', { de: de.toISOString(), ate: ate.toISOString(), recurso_id: recursoId || undefined }),
  });
}

export function deDiaLocal(dia: string): Date {
  const [a, m, d] = dia.split('-').map(Number) as [number, number, number];
  return new Date(a, m - 1, d);
}

/** rac-3 (SPEC-B1): sem nome, o rótulo neutro de cadastro de outra empresa, que não é nome de ninguém e não vira iniciais. */
export const nomeDoProfissional = (nome: string | null | undefined) => (nome ? nomePessoa(nome) : nomeCadastro(nome));

/**
 * Nome do recurso em toda a Agenda. Rodada rge-12 (VIS12-M1) e rac-2 (SPEC-M1): profissional é pessoa, com ou sem usuário, e o modo
 * discreto mascara o nome; sala e equipamento ficam. Na dúvida (lista carregando, recurso arquivado ou de outra empresa), mascara.
 */
export function useNomeDoRecurso() {
  const recursos = useRecursos(false);
  return useMemo(() => {
    const tipos = new Map((recursos.data ?? []).map((r) => [r.id, r.tipo]));
    return (id: string, nome: string | null | undefined) => (tipos.has(id) && tipos.get(id) !== 'profissional' ? nomeCadastro(nome) : nomeDoProfissional(nome));
  }, [recursos.data]);
}
